import {Constants} from '@/utils/constants/commonConstants'
import {FEEDBACK_TYPE, UPLOAD_TYPE, VARIANT} from '@/utils/constants/globalStyle'
import {File as PepsiCoDsFile} from 'pepsico-ds'

/**
 * File component for uploading files with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.acceptedExtensions - The accepted file extensions.
 * @param {string} props.className - Additional class names for styling.
 * @param {boolean} [props.disabled=false] - Whether the file upload is disabled.
 * @param {string} props.label - The label for the file upload input.
 * @param {Array} props.value - The files to be uploaded.
 * @param {boolean} [props.isDark=true] - Whether the component is in dark mode.
 * @param {boolean} [props.multiple=false] - Whether multiple file uploads are allowed.
 * @param {string} [props.variant=VARIANT.DEFAULT] - The variant of the file upload component.
 * @param {string} [props.containerClassName=null] - Additional class names for the container.
 * @param {number|null} [props.progressPercentage=null] - The upload progress percentage.
 * @param {string} [props.feedbackType=FEEDBACK_TYPE.INFO] - The type of feedback to display.
 * @param {string} [props.helperText=''] - Helper text for the file upload input.
 * @param {string} [props.type=UPLOAD_TYPE.SINGLE] - The type of upload (single or multiple).
 * @param {Function} [props.onUpload=() => {}] - Callback function when a file is uploaded.
 * @param {Function} [props.onUploadDelete=() => {}] - Callback function when a file is deleted.
 * @param {Function} [props.onUploadError=() => {}] - Callback function when there is an upload error.
 * @param {string} [props.description=''] - Description for the file upload input.
 * @param {string} [props.imageUri=''] - URI of the image to be displayed.
 * @param {boolean} [props.required=false] - Whether the file upload is required.
 * @param {string} [props.buttonLabel=Constants.UploadFile] - The label for the upload button.
 *
 * @returns {JSX.Element} The rendered File component.
 */

export default function File({
  acceptedExtensions,
  className,
  disabled = false,
  label,
  value: files,
  isDark = true,
  multiple = false,
  variant = VARIANT.DEFAULT,
  containerClassName = null,
  progressPercentage = null,
  feedbackType = FEEDBACK_TYPE.INFO,
  helperText = '',
  type = UPLOAD_TYPE.SINGLE,
  onUpload = () => {},
  onUploadDelete = () => {},
  onUploadError = () => {},
  description = '',
  imageUri = '',
  required = false,
  buttonLabel = Constants.UploadFile,
  dataTestId = '',
}) {
  return (
    <PepsiCoDsFile
      data-testid={dataTestId || 'file-uploader'}
      className={className}
      style={{width: '100%'}}
      accept={acceptedExtensions}
      description={description}
      label={label}
      variant={variant}
      files={files || []}
      onUpdate={onUpload}
      onDelete={onUploadDelete}
      multiple={multiple}
      isDark={isDark || false}
      disabled={disabled}
      helperText={helperText}
      type={type}
      onFileError={onUploadError}
      containerClassName={containerClassName}
      progressPerc={progressPercentage}
      feedbackType={feedbackType}
      imageUri={imageUri}
      required={required}
      buttonLabel={buttonLabel}
    />
  )
}
